import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PrintFileReviewResult } from '../../api/printFileBatches';
import {
  applyBackgroundColor,
  composeFinalBlob,
  resizeCanvas,
  rotateCanvas,
  trimCanvas,
  trimCanvasByColor,
} from '../../lib/printFileReview/canvasUtils';
import {
  assetState,
  defaultVariant,
  ensureAsset,
  finalChoice,
  getStoredBlob,
  hasUpscaled,
  packDownloadProgress,
} from '../../lib/printFileReview/assets';
import { decideFile, revertFileEdit, saveFileEdit, setBatchIndex } from '../../lib/printFileReview/batches';
import { subscribeReviewStore } from '../../lib/printFileReview/events';
import {
  alignObjectToDraft,
  bgColorFor,
  blobToCanvas,
  canvasToBlob,
  capEditingMaster,
  cloneCanvas,
  IOutputGeometry,
  pxToResizeInches,
  resolveOutputGeometry,
} from '../../lib/printFileReview/imageOps';
import { addEditLog, getFileLogs } from '../../lib/printFileReview/logs';
import { fmtDateTime } from '../../lib/printFileReview/packDetails';
import { MAX_AUTO_ATTEMPTS, retryAllNow, setRetriesStopped } from '../../lib/printFileReview/sync';
import { BlobKind, FileVariant, IEditLogEntry, ILocalBatch, ILocalFile } from '../../lib/printFileReview/types';
import { usePackDetails } from './usePackDetails';

// Brush size range in screen pixels (slider and [ / ] keys)
const BRUSH_SIZE_MIN = 1;
const BRUSH_SIZE_MAX = 50;

// ── Crop helper utilities (ported from the former print-ready-files editor) ───
type CropHitZone = 'nw' | 'n' | 'ne' | 'w' | 'e' | 'sw' | 's' | 'se' | 'move' | null;

function getCropHitZone(
  cx: number, cy: number,
  rect: { x: number; y: number; w: number; h: number },
  canvasWidth: number,
): CropHitZone {
  const { x, y, w, h } = rect;
  const t = Math.max(14, Math.round(canvasWidth / 60));
  if (Math.abs(cx - x) < t && Math.abs(cy - y) < t) return 'nw';
  if (Math.abs(cx - (x + w)) < t && Math.abs(cy - y) < t) return 'ne';
  if (Math.abs(cx - x) < t && Math.abs(cy - (y + h)) < t) return 'sw';
  if (Math.abs(cx - (x + w)) < t && Math.abs(cy - (y + h)) < t) return 'se';
  if (Math.abs(cy - y) < t && cx > x + t && cx < x + w - t) return 'n';
  if (Math.abs(cy - (y + h)) < t && cx > x + t && cx < x + w - t) return 's';
  if (Math.abs(cx - x) < t && cy > y + t && cy < y + h - t) return 'w';
  if (Math.abs(cx - (x + w)) < t && cy > y + t && cy < y + h - t) return 'e';
  if (cx > x && cx < x + w && cy > y && cy < y + h) return 'move';
  return null;
}

function getCropCursorStyle(zone: CropHitZone): string {
  switch (zone) {
    case 'nw': case 'se': return 'nwse-resize';
    case 'ne': case 'sw': return 'nesw-resize';
    case 'n': case 's': return 'ns-resize';
    case 'e': case 'w': return 'ew-resize';
    case 'move': return 'move';
    default: return 'crosshair';
  }
}

const DECISION_LABEL: Record<PrintFileReviewResult, string> = {
  ok: 'OK',
  need_design: 'Need design',
  need_cs: 'Need CS',
};

/** "1378x2376" → `19.1" × 33.0"` (72 DPI print files). */
const formatInches = (px?: string): string => {
  if (!px) return '--';
  const [w, h] = px.split('x').map(Number);
  if (!w || !h) return '--';
  return `${(w / 72).toFixed(1)}" × ${(h / 72).toFixed(1)}"`;
};

const syncText = (f: ILocalFile, retriesStopped: boolean): { text: string; cls: string } | null => {
  switch (f.sync.state) {
    case 'queued': return { text: 'Queued for saving…', cls: 'text-gray-400' };
    case 'uploading': return { text: 'Uploading edited file to S3…', cls: 'text-blue-300' };
    case 'saving': return { text: 'Saving review…', cls: 'text-blue-300' };
    case 'synced': return { text: 'Saved on server', cls: 'text-green-400' };
    case 'error': {
      const why = retriesStopped || f.sync.parked === 'stopped'
        ? 'auto-retry stopped'
        : f.sync.parked === 'limit'
          ? `stopped after ${MAX_AUTO_ATTEMPTS} attempts — use Retry now`
          : f.sync.parked === 'permanent' ? 'use Retry now' : 'retrying';
      return { text: `Not saved: ${f.sync.error || 'error'} — ${why}`, cls: 'text-red-400' };
    }
    default: return null;
  }
};

const fmtTime = (ts: number) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const fmtDetails = (d?: Record<string, unknown>) => {
  if (!d) return '';
  const s = JSON.stringify(d);
  return s.length > 160 ? `${s.slice(0, 157)}…` : s;
};

interface IReviewModalProps {
  batch: ILocalBatch;
  files: ILocalFile[];
  /** Batch waiting on this device to continue with (prefetched or older unfinished one). */
  nextBatch: ILocalBatch | null;
  nextBatchFiles: ILocalFile[];
  requestingNext: boolean;
  /** Automatic retries of failed saves are switched off. */
  retriesStopped: boolean;
  /** Moderators see each file's history from the server (pipeline runs, reviews). */
  showServerHistory: boolean;
  onClose: () => void;
  onOpenBatch: (batchId: string) => void;
  onRequestNext: () => void;
}

const ReviewModal: React.FC<IReviewModalProps> = ({
  batch, files, nextBatch, nextBatchFiles, requestingNext, retriesStopped, showServerHistory, onClose, onOpenBatch, onRequestNext,
}) => {
  const editCanvasRef = useRef<HTMLCanvasElement>(null);
  const editContainerRef = useRef<HTMLDivElement>(null);
  const editWorkingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);

  // A pack that was already done when opened, or someone else's pack, is only looked at:
  // no edits, no decision changes — and it always starts from the first file.
  const [readOnly] = useState(() => batch.serverStatus === 'done' || !!batch.viewOnly);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(() =>
    readOnly ? 0 : Math.min(Math.max(0, batch.currentIndex || 0), Math.max(0, files.length - 1)));
  // Reviewed files of done / view-only packs are shown from the server finals, not the drafts.
  const showFinals = batch.serverStatus === 'done' || !!batch.viewOnly;
  const [reviewPreviewBg, setReviewPreviewBg] = useState<'transparent' | 'white' | 'black'>('transparent');
  const [downloading, setDownloading] = useState(false);
  const [fileLogs, setFileLogs] = useState<IEditLogEntry[]>([]);

  // Loupe (magnifier) over the print-ready image in review mode
  const LOUPE_SIZE = 380;
  const LOUPE_ZOOM = 2; // ~200%, like Adobe Bridge
  const [loupeEnabled, setLoupeEnabled] = useState(true);
  const [loupePos, setLoupePos] = useState<{ x: number; y: number; imgX: number; imgY: number } | null>(null);
  const [reviewNatural, setReviewNatural] = useState<{ w: number; h: number } | null>(null);
  const reviewImgRef = useRef<HTMLImageElement>(null);
  const reviewImgWrapRef = useRef<HTMLDivElement>(null);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editPreparing, setEditPreparing] = useState(false);
  const [editNotice, setEditNotice] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [editTool, setEditTool] = useState<'crop' | 'brush' | 'rotate' | null>(null);
  const [editBrushColor, setEditBrushColor] = useState<'white' | 'black'>('white');
  const [editBrushSize, setEditBrushSize] = useState(10);
  const [editBrushHardness, setEditBrushHardness] = useState(85);
  // True when the editor holds the TRANSPARENT object (the server's background-removed
  // file); the brush then acts as an eraser and rotation spins only the object.
  const [editIsTransparent, setEditIsTransparent] = useState(false);
  const editIsTransparentRef = useRef(false);
  const [editRotationAngle, setEditRotationAngle] = useState(0);
  const [editHasChanges, setEditHasChanges] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [cropApplied, setCropApplied] = useState(false);
  const [rotateApplied, setRotateApplied] = useState(false);
  // Output geometry (size + black frame) measured from the draft on entering the editor
  const editGeometryRef = useRef<IOutputGeometry | null>(null);
  const editSourceRef = useRef<string>('');
  // Variant (regular / upscaled) the editor was opened on — the edit is saved for it.
  const editVariantRef = useRef<FileVariant>('regular');
  const editOpsRef = useRef({ crop: 0, brush: 0, rotate: 0, undo: 0, redo: 0 });

  // Enhanced crop (resizable rectangle overlay)
  const cropRectRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);
  const [cropRectUI, setCropRectUI] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const cropDragModeRef = useRef<string | null>(null);
  const cropDragOriginRef = useRef<{ mouseX: number; mouseY: number; rect: { x: number; y: number; w: number; h: number } } | null>(null);

  // Mouse rotation: the object follows the cursor around the canvas center
  // (angular drag, like Photoshop free transform)
  const rotateMouseRef = useRef<{ cx: number; cy: number; startMouseAngle: number; startAngle: number } | null>(null);

  // Consecutive rotation Applies are coalesced: we keep a snapshot from before
  // the FIRST apply and re-rotate it by the accumulated total, so ten small
  // "Apply" steps cost ONE resample instead of ten. Any other edit clears it.
  const rotateBaseRef = useRef<{ snap: HTMLCanvasElement; total: number } | null>(null);
  const invalidateRotateBase = () => { rotateBaseRef.current = null; };

  // Zoom & pan
  const [editZoom, setEditZoom] = useState(1);
  const [editPan, setEditPan] = useState({ x: 0, y: 0 });
  const isPanningRef = useRef(false);
  const panOriginRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);
  const [editCursorStyle, setEditCursorStyle] = useState('default');
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);
  const isSpaceHeldRef = useRef(false);

  // Undo / redo history (canvas snapshots)
  const editHistoryRef = useRef<HTMLCanvasElement[]>([]);
  const editHistoryIdxRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const currentReview: ILocalFile | undefined = files[currentReviewIndex];
  const currentReviewRef = useRef(currentReview);
  currentReviewRef.current = currentReview;

  const logEdit = useCallback((action: string, details?: Record<string, unknown>) => {
    const f = currentReviewRef.current;
    if (!f) return;
    void addEditLog({ batchId: f.batchId, fileId: f.id, action, details });
  }, []);

  // Remember where the designer is, so the batch reopens on the same file
  // (and the halfway prefetch fires). Viewing a pack changes nothing.
  useEffect(() => {
    if (!readOnly) void setBatchIndex(batch.id, currentReviewIndex);
  }, [batch.id, currentReviewIndex, readOnly]);

  // ── Images of the WHOLE set held as object URLs (local IndexedDB copies), so moving
  // between print files — and between the regular and upscaled variant — never waits on
  // storage or the network. Server URLs are only a fallback for images that failed to download.
  type FileUrls = { draft: string; draftUpscaled: string | null; edit: string | null; serverFinal: string | null; original: string; mockup: string | null };
  const [packUrls, setPackUrls] = useState<Record<string, FileUrls>>({});
  const urlCacheRef = useRef<Map<string, { token: string; url: string }>>(new Map());
  const staleUrlsRef = useRef<string[]>([]);
  const packKey = files
    .map((f) => [
      f.id,
      f.finalAsset?.state ?? '',
      f.finalAsset?.url ?? '',
      f.assets.draft.state,
      assetState(f, 'draftUpscaled'),
      f.assets.original.state,
      f.assets.mockup.state,
      f.edit?.savedAt ?? 0,
    ].join(':'))
    .join('|');

  useEffect(() => {
    let cancelled = false;
    const cache = urlCacheRef.current;
    const localUrl = async (fileId: string, kind: BlobKind, token: string): Promise<string | null> => {
      const key = `${fileId}:${kind}`;
      const hit = cache.get(key);
      if (hit && hit.token === token) return hit.url;
      if (hit) {
        staleUrlsRef.current.push(hit.url);
        cache.delete(key);
      }
      const blob = await getStoredBlob(fileId, kind);
      if (!blob) return null;
      const url = URL.createObjectURL(blob);
      cache.set(key, { token, url });
      return url;
    };
    (async () => {
      const next: Record<string, FileUrls> = {};
      await Promise.all(files.map(async (f) => {
        const [draft, draftUpscaled, edit, serverFinal, original, mockup] = await Promise.all([
          localUrl(f.id, 'draft', f.assets.draft.state),
          hasUpscaled(f) ? localUrl(f.id, 'draftUpscaled', assetState(f, 'draftUpscaled')) : null,
          f.edit ? localUrl(f.id, 'final', String(f.edit.savedAt)) : null,
          f.finalAsset?.state === 'ok' ? localUrl(f.id, 'serverFinal', f.finalAsset.url ?? '') : null,
          localUrl(f.id, 'original', f.assets.original.state),
          localUrl(f.id, 'mockup', f.assets.mockup.state),
        ]);
        next[f.id] = {
          draft: draft ?? f.draftPrintReadyUrl ?? '',
          draftUpscaled: draftUpscaled ?? f.draftPrintReadyFileUpscaledUrl ?? null,
          edit,
          serverFinal,
          original: original ?? f.originalUrl ?? '',
          mockup: mockup ?? f.mockupUrl ?? null,
        };
      }));
      if (cancelled) return;
      setPackUrls(next);
      // Replaced URLs (e.g. a new edit) are released once nothing shows them.
      const stale = staleUrlsRef.current;
      staleUrlsRef.current = [];
      setTimeout(() => stale.forEach((u) => URL.revokeObjectURL(u)), 1000);
    })().catch(() => undefined);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packKey]);

  useEffect(() => () => {
    urlCacheRef.current.forEach(({ url }) => URL.revokeObjectURL(url));
    urlCacheRef.current.clear();
    staleUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  // Regular / upscaled variant being looked at, per file. Until the designer switches it:
  // the variant the decision was given to, else regular.
  const [viewVariants, setViewVariants] = useState<Record<string, FileVariant>>({});
  const variantOf = (f: ILocalFile): FileVariant => (hasUpscaled(f) ? viewVariants[f.id] ?? defaultVariant(f) : 'regular');

  /** The print-ready image shown for a file, and what it is. */
  const shownOf = (f: ILocalFile | undefined): { src: string; kind: 'serverFinal' | 'edit' | 'upscaled' | 'draft'; remoteEdit: boolean } => {
    const u = f && packUrls[f.id];
    if (!f || !u) return { src: '', kind: 'draft', remoteEdit: false };
    // Done / view-only sets show what the server keeps as the final.
    if (showFinals && u.serverFinal) return { src: u.serverFinal, kind: 'serverFinal', remoteEdit: false };
    const choice = finalChoice(f, variantOf(f));
    if (choice === 'edit' && u.edit) return { src: u.edit, kind: 'edit', remoteEdit: false };
    if (choice === 'remoteEdit') return { src: u.serverFinal ?? f.sync.finalPrintReadyUrl ?? u.draft, kind: 'serverFinal', remoteEdit: true };
    if (choice === 'upscaled' && u.draftUpscaled) return { src: u.draftUpscaled, kind: 'upscaled', remoteEdit: false };
    return { src: u.draft, kind: 'draft', remoteEdit: false };
  };

  const currentUrls = currentReview ? packUrls[currentReview.id] : undefined;
  const currentVariant: FileVariant = currentReview ? variantOf(currentReview) : 'regular';
  const currentShown = shownOf(currentReview);
  const reviewResultSrc = currentShown.src;
  const reviewOriginalSrc = currentUrls?.original ?? '';
  const reviewMockupSrc = currentUrls?.mockup ?? null;

  // Decode the current and neighbouring files ahead of time (and both variants of the
  // current one), so switching paints at once.
  const decodedRef = useRef<Map<string, HTMLImageElement>>(new Map());
  useEffect(() => {
    const keep = new Set<string>();
    const warm = (src: string | null | undefined) => {
      if (!src || !src.startsWith('blob:')) return;
      keep.add(src);
      if (decodedRef.current.has(src)) return;
      const img = new Image();
      img.src = src;
      img.decode().catch(() => undefined);
      decodedRef.current.set(src, img);
    };
    for (let d = -1; d <= 2; d++) {
      const f = files[currentReviewIndex + d];
      const u = f && packUrls[f.id];
      if (!f || !u) continue;
      [shownOf(f).src, u.original, u.mockup].forEach(warm);
      if (d === 0) [u.draft, u.draftUpscaled].forEach(warm);
    }
    decodedRef.current.forEach((_img, src) => { if (!keep.has(src)) decodedRef.current.delete(src); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReviewIndex, packUrls, files, viewVariants]);

  // Edit log of the current file (live)
  const currentId = currentReview?.id;
  useEffect(() => {
    if (!currentId) return;
    let alive = true;
    const load = () => { getFileLogs(currentId).then((l) => alive && setFileLogs(l)).catch(() => undefined); };
    load();
    const unsubscribe = subscribeReviewStore(load);
    return () => { alive = false; unsubscribe(); };
  }, [currentId]);

  // Brush cursor (CSS custom cursor). The circle's OUTER diameter equals the
  // full brush extent on screen: the stroke is opaque out to hardness × radius
  // and fades to nothing at the circle, so nothing is painted outside it.
  const brushCursorCss = React.useMemo(() => {
    if (editTool !== 'brush') return '';
    const size = Math.max(editBrushSize, 4);
    const svgSize = size + 2;
    const c = svgSize / 2;
    const r = (size - 1) / 2; // 1px stroke centered on r → outer edge = size/2
    const sc = editBrushColor === 'white' ? 'white' : 'black';
    const oc = editBrushColor === 'white' ? 'black' : 'white';
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${svgSize}' height='${svgSize}'><circle cx='${c}' cy='${c}' r='${r}' fill='none' stroke='${oc}' stroke-width='2' opacity='0.35'/><circle cx='${c}' cy='${c}' r='${r}' fill='none' stroke='${sc}' stroke-width='1'/><circle cx='${c}' cy='${c}' r='1' fill='${sc}' opacity='0.6'/></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svg)}") ${Math.round(c)} ${Math.round(c)}, crosshair`;
  }, [editTool, editBrushSize, editBrushColor]);

  // ── Soft brush (vector stroke, adjustable hardness) ──────────────
  // The whole stroke is one continuous path with round caps drawn onto a
  // scratch canvas (softened with a blur filter), composited over a snapshot
  // of the pre-stroke canvas. No stamping → no scalloped edges at any zoom
  // or brush size, and no opacity build-up along the stroke.
  const brushBackupRef = useRef<HTMLCanvasElement | null>(null);
  const brushScratchRef = useRef<HTMLCanvasElement | null>(null);
  const brushPointsRef = useRef<{ x: number; y: number }[]>([]);
  const brushStrokeSizeRef = useRef(10);

  const renderBrushStroke = () => {
    const wc = editWorkingCanvasRef.current;
    const backup = brushBackupRef.current;
    const pts = brushPointsRef.current;
    if (!wc || !backup || !pts.length) return;
    let scratch = brushScratchRef.current;
    if (!scratch || scratch.width !== wc.width || scratch.height !== wc.height) {
      scratch = document.createElement('canvas');
      scratch.width = wc.width;
      scratch.height = wc.height;
      brushScratchRef.current = scratch;
    }
    const size = brushStrokeSizeRef.current;
    // Photoshop-style hardness: fully opaque out to hardness × radius, fading
    // to ~0 exactly at the radius — so the whole stroke stays inside the cursor
    // circle. The ramp is a Gaussian (CSS blur σ) centred on the core edge,
    // ±2σ wide: core/2 − 2σ = h·r and core/2 + 2σ = r.
    const h = editBrushHardness / 100;
    const blur = (size * (1 - h)) / 8;
    const core = Math.max(0.5, (size * (1 + h)) / 2);
    const sctx = scratch.getContext('2d')!;
    sctx.clearRect(0, 0, scratch.width, scratch.height);
    sctx.filter = blur > 0.05 ? `blur(${blur.toFixed(2)}px)` : 'none';
    sctx.strokeStyle = editBrushColor;
    sctx.fillStyle = editBrushColor;
    sctx.lineCap = 'round';
    sctx.lineJoin = 'round';
    if (pts.length === 1) {
      sctx.beginPath();
      sctx.arc(pts[0].x, pts[0].y, core / 2, 0, Math.PI * 2);
      sctx.fill();
    } else {
      sctx.lineWidth = core;
      sctx.beginPath();
      sctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) sctx.lineTo(pts[i].x, pts[i].y);
      sctx.stroke();
    }
    sctx.filter = 'none';
    const wctx = wc.getContext('2d')!;
    wctx.clearRect(0, 0, wc.width, wc.height);
    wctx.drawImage(backup, 0, 0);
    // On the transparent object the brush is an ERASER: the stroke's alpha
    // (including its soft edge) removes object pixels instead of painting.
    if (editIsTransparentRef.current) wctx.globalCompositeOperation = 'destination-out';
    wctx.drawImage(scratch, 0, 0);
    wctx.globalCompositeOperation = 'source-over';
  };

  const finishBrushStroke = () => {
    brushBackupRef.current = null;
    brushPointsRef.current = [];
  };

  // Scale the rotation preview so the ROTATED bounding box fits the editor
  // viewport. Sized against the container (not the original canvas box), so a
  // 90° turn shows the swapped aspect at full size instead of shrinking.
  const rotateFitScale = React.useMemo(() => {
    if (editTool !== 'rotate' || editRotationAngle === 0) return 1;
    const canvas = editCanvasRef.current;
    const container = editContainerRef.current;
    if (!canvas || !container) return 1;
    const dw = canvas.offsetWidth;
    const dh = canvas.offsetHeight;
    if (!dw || !dh) return 1;
    const rad = (Math.abs(editRotationAngle) * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rad));
    const sin = Math.abs(Math.sin(rad));
    const bboxW = dw * cos + dh * sin;
    const bboxH = dw * sin + dh * cos;
    const cw = Math.max(1, container.clientWidth - 8);
    const ch = Math.max(1, container.clientHeight - 8);
    return Math.min(cw / bboxW, ch / bboxH, 2.5);
  }, [editTool, editRotationAngle]);

  // ── Loupe (200% magnifier over the print-ready image) ────────────
  const handleLoupeMove = (e: React.MouseEvent) => {
    if (!loupeEnabled) return;
    const img = reviewImgRef.current;
    const wrap = reviewImgWrapRef.current;
    if (!img || !wrap) return;
    const ir = img.getBoundingClientRect();
    const wr = wrap.getBoundingClientRect();
    if (e.clientX < ir.left || e.clientX > ir.right || e.clientY < ir.top || e.clientY > ir.bottom) {
      setLoupePos(null);
      return;
    }
    const nw = img.naturalWidth || 1;
    const nh = img.naturalHeight || 1;
    const imgX = ((e.clientX - ir.left) / ir.width) * nw;
    const imgY = ((e.clientY - ir.top) / ir.height) * nh;
    let x = e.clientX - wr.left - LOUPE_SIZE / 2;
    let y = e.clientY - wr.top - LOUPE_SIZE / 2;
    x = Math.max(4, Math.min(wr.width - LOUPE_SIZE - 4, x));
    y = Math.max(4, Math.min(wr.height - LOUPE_SIZE - 4, y));
    setLoupePos({ x, y, imgX, imgY });
  };

  const reviewFilename = currentReview?.orderNumber || '--';
  const reviewSKU = currentReview?.sku || 'N/A';
  const reviewColor = currentReview?.color || 'transparent';
  const reviewOriginalSize = currentReview?.assets.original.width
    ? `${currentReview.assets.original.width}x${currentReview.assets.original.height}`
    : '--';
  const shownDims = !currentReview ? undefined
    : currentShown.kind === 'serverFinal' ? currentReview.finalAsset
    : currentShown.kind === 'edit' ? currentReview.edit
    : currentShown.kind === 'upscaled' ? currentReview.assets.draftUpscaled
    : currentReview.assets.draft;
  const reviewResultSize = shownDims?.width
    ? `${shownDims.width}x${shownDims.height}`
    : reviewNatural ? `${reviewNatural.w}x${reviewNatural.h}` : undefined;
  // What the screen shows instead of the plain draft: a manual edit (made here or in another browser) or the upscaled draft.
  const showingEdit = currentShown.kind === 'edit' || currentShown.remoteEdit;
  const shownLabel = showFinals && currentShown.kind === 'serverFinal' ? ' (final)'
    : showingEdit ? ' (edited)'
    : currentShown.kind === 'upscaled' ? ' (upscaled)' : '';
  // What the saved decision stands for.
  const decidedFinal = currentReview?.decision ? finalChoice(currentReview, currentReview.decidedVariant ?? 'regular') : null;
  const reviewCroppingNotes = currentReview?.croppingNotes;
  const countOk = files.filter((r) => r.decision === 'ok').length;
  const countDesign = files.filter((r) => r.decision === 'need_design').length;
  const countCs = files.filter((r) => r.decision === 'need_cs').length;
  const decidedCount = files.filter((r) => r.decision).length;
  const syncedCount = files.filter((r) => r.decision && r.sync.state === 'synced').length;
  const syncErrorCount = files.filter((r) => r.sync.state === 'error').length;
  const allDecided = files.length > 0 && decidedCount === files.length;
  const currentSync = currentReview ? syncText(currentReview, retriesStopped) : null;

  const nextProgress = packDownloadProgress(nextBatch, nextBatchFiles);

  const rateImage = (decision: PrintFileReviewResult) => {
    if (!currentReview || editMode || readOnly) return;
    // The decision is given to the variant on screen (regular or upscaled).
    void decideFile(currentReview, decision, variantOf(currentReview));
    setTimeout(() => {
      if (currentReviewIndex < files.length - 1) {
        setCurrentReviewIndex((i) => i + 1);
      }
    }, 200);
  };

  const handlePrev = () => { if (currentReviewIndex > 0) setCurrentReviewIndex((i) => i - 1); };
  const handleNext = () => { if (currentReviewIndex < files.length - 1) setCurrentReviewIndex((i) => i + 1); };

  // Arrow-key navigation while reviewing (disabled inside the editor)
  useEffect(() => {
    if (editMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setCurrentReviewIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setCurrentReviewIndex((i) => Math.min(files.length - 1, i + 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, files.length]);

  /** Download the print-ready file on screen: the server final, the manual edit, the upscaled draft or the draft. */
  const handleDownloadCurrent = async () => {
    const item = currentReview;
    if (!item || downloading) return;
    setDownloading(true);
    try {
      const shown = shownOf(item);
      const blob = (shown.kind === 'serverFinal' ? await getStoredBlob(item.id, 'serverFinal') : null)
        ?? (shown.kind === 'edit' ? await getStoredBlob(item.id, 'final') : null)
        ?? (shown.kind === 'upscaled' ? await ensureAsset(item, 'draftUpscaled') : null)
        ?? (await ensureAsset(item, 'draft'));
      if (!blob) throw new Error('The print-ready file is not available');
      const ext = blob.type === 'image/png' ? 'png' : 'jpg';
      const suffix = shown.kind === 'serverFinal' ? (shown.remoteEdit ? '_edited' : '_final')
        : shown.kind === 'edit' ? '_edited'
        : shown.kind === 'upscaled' ? '_upscaled' : '';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${item.orderNumber || 'order'}_${item.id}${suffix}.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e: any) {
      setEditError(e?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  // ====== Edit Mode Functions ======

  const getCanvasCoords = (e: React.MouseEvent<HTMLCanvasElement>): { x: number; y: number } => {
    const canvas = editCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const drawEditOverlay = () => {
    const workCanvas = editWorkingCanvasRef.current;
    const displayCanvas = editCanvasRef.current;
    if (!workCanvas || !displayCanvas) return;
    const cw = workCanvas.width;
    const ch = workCanvas.height;
    displayCanvas.width = cw;
    displayCanvas.height = ch;
    const ctx = displayCanvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(workCanvas, 0, 0);

    // ── Crop overlay ──
    const cr = cropRectRef.current;
    if (editTool === 'crop' && cr) {
      // Semi-transparent dark overlay outside crop
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, cw, cr.y);
      ctx.fillRect(0, cr.y, cr.x, cr.h);
      ctx.fillRect(cr.x + cr.w, cr.y, cw - cr.x - cr.w, cr.h);
      ctx.fillRect(0, cr.y + cr.h, cw, ch - cr.y - cr.h);
      // White border
      const lw = Math.max(1, Math.round(cw / 600));
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = lw;
      ctx.strokeRect(cr.x, cr.y, cr.w, cr.h);
      // Rule of thirds grid
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = Math.max(1, lw * 0.5);
      for (let i = 1; i <= 2; i++) {
        ctx.beginPath(); ctx.moveTo(cr.x + (cr.w * i) / 3, cr.y); ctx.lineTo(cr.x + (cr.w * i) / 3, cr.y + cr.h); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cr.x, cr.y + (cr.h * i) / 3); ctx.lineTo(cr.x + cr.w, cr.y + (cr.h * i) / 3); ctx.stroke();
      }
      // Dimension label
      ctx.font = `${Math.max(12, Math.round(cw / 50))}px monospace`;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.textAlign = 'center';
      const lblY = cr.y + cr.h + Math.max(18, Math.round(cw / 35));
      if (lblY < ch - 5) ctx.fillText(`${Math.round(cr.w)} × ${Math.round(cr.h)}`, cr.x + cr.w / 2, lblY);
      // Resize handles (8 squares)
      const hs = Math.max(8, Math.round(cw / 100));
      const hps: [number, number][] = [
        [cr.x, cr.y], [cr.x + cr.w / 2, cr.y], [cr.x + cr.w, cr.y],
        [cr.x, cr.y + cr.h / 2], [cr.x + cr.w, cr.y + cr.h / 2],
        [cr.x, cr.y + cr.h], [cr.x + cr.w / 2, cr.y + cr.h], [cr.x + cr.w, cr.y + cr.h],
      ];
      ctx.fillStyle = '#ffffff';
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 1;
      for (const [hx, hy] of hps) {
        ctx.fillRect(hx - hs / 2, hy - hs / 2, hs, hs);
        ctx.strokeRect(hx - hs / 2, hy - hs / 2, hs, hs);
      }
    }
  };

  // Ref for latest drawEditOverlay (for use in effects with stale closure prevention)
  const drawOverlayRef = useRef(drawEditOverlay);
  drawOverlayRef.current = drawEditOverlay;

  // Redraw overlay when edit tool or settings change
  useEffect(() => {
    if (editMode) {
      const id = requestAnimationFrame(() => drawOverlayRef.current());
      return () => cancelAnimationFrame(id);
    }
  }, [editMode, editTool]);

  // ── Undo / Redo helpers ──────────────────────────────────────────
  const EDIT_HISTORY_LIMIT = 30;

  const syncHistoryUI = () => {
    const idx = editHistoryIdxRef.current;
    setCanUndo(idx > 0);
    setCanRedo(idx < editHistoryRef.current.length - 1);
    setEditHasChanges(idx > 0);
  };

  /** Save current working canvas state to history (call AFTER an edit). */
  const pushEditHistory = () => {
    const wc = editWorkingCanvasRef.current;
    if (!wc) return;
    const idx = editHistoryIdxRef.current;
    // Discard any redo (future) states
    editHistoryRef.current = editHistoryRef.current.slice(0, idx + 1);
    // Clone canvas
    const snap = document.createElement('canvas');
    snap.width = wc.width;
    snap.height = wc.height;
    snap.getContext('2d')!.drawImage(wc, 0, 0);
    editHistoryRef.current.push(snap);
    // Trim oldest entries if over limit
    if (editHistoryRef.current.length > EDIT_HISTORY_LIMIT) {
      editHistoryRef.current.shift();
    }
    editHistoryIdxRef.current = editHistoryRef.current.length - 1;
    syncHistoryUI();
  };

  /** Restore canvas from snapshot at current history index. */
  const restoreFromHistory = () => {
    const snap = editHistoryRef.current[editHistoryIdxRef.current];
    const wc = editWorkingCanvasRef.current;
    if (!snap || !wc) return;
    invalidateRotateBase();
    wc.width = snap.width;
    wc.height = snap.height;
    wc.getContext('2d')!.drawImage(snap, 0, 0);
    // If crop tool is active, reset crop rect to match restored canvas size
    if (editTool === 'crop') {
      cropRectRef.current = { x: 0, y: 0, w: snap.width, h: snap.height };
      setCropRectUI({ x: 0, y: 0, w: snap.width, h: snap.height });
    }
    syncHistoryUI();
    drawEditOverlay();
  };

  const editUndo = () => {
    if (editHistoryIdxRef.current <= 0) return;
    editHistoryIdxRef.current--;
    restoreFromHistory();
    editOpsRef.current.undo += 1;
    logEdit('edit_undo', { step: editHistoryIdxRef.current });
  };

  const editRedo = () => {
    if (editHistoryIdxRef.current >= editHistoryRef.current.length - 1) return;
    editHistoryIdxRef.current++;
    restoreFromHistory();
    editOpsRef.current.redo += 1;
    logEdit('edit_redo', { step: editHistoryIdxRef.current });
  };

  // Stable refs for undo/redo (used in keyboard effect to avoid stale closures)
  const editUndoRef = useRef(editUndo);
  editUndoRef.current = editUndo;
  const editRedoRef = useRef(editRedo);
  editRedoRef.current = editRedo;

  /** Finish the current brush stroke: log it, keep it in history, redraw. */
  const endBrushStroke = () => {
    const wc = editWorkingCanvasRef.current;
    editOpsRef.current.brush += 1;
    logEdit('edit_brush', {
      mode: editIsTransparentRef.current ? 'eraser' : `paint_${editBrushColor}`,
      sizeScreenPx: editBrushSize,
      sizeImagePx: Math.round(brushStrokeSizeRef.current * 10) / 10,
      hardness: editBrushHardness,
      points: brushPointsRef.current.length,
      canvas: wc ? { w: wc.width, h: wc.height } : null,
    });
    finishBrushStroke();
    pushEditHistory();
  };
  const endBrushStrokeRef = useRef(endBrushStroke);
  endBrushStrokeRef.current = endBrushStroke;

  const resetEditState = () => {
    setEditTool(null);
    setEditHasChanges(false);
    invalidateRotateBase();
    setCanUndo(false);
    setCanRedo(false);
    editHistoryRef.current = [];
    editHistoryIdxRef.current = -1;
    cropRectRef.current = null;
    setCropRectUI(null);
    cropDragModeRef.current = null;
    cropDragOriginRef.current = null;
    setEditZoom(1);
    setEditPan({ x: 0, y: 0 });
    setEditCursorStyle('default');
    rotateMouseRef.current = null;
  };

  const enterEditMode = async () => {
    const file = currentReview;
    if (!file || editPreparing || readOnly) return;
    setEditPreparing(true);
    setEditNotice(null);
    setEditError(null);
    try {
      const bg = bgColorFor(file.color);
      // Edit the variant on screen: the upscaled draft has its own background-removed file.
      const variant = variantOf(file);
      const upscaled = variant === 'upscaled';
      editVariantRef.current = variant;
      // An edit made in this browser from this same variant can be continued.
      const localEdit = !!file.edit && (file.edit.variant ?? 'regular') === variant;
      const editedElsewhere = finalChoice(file, variant) === 'remoteEdit';
      // The draft the reviewer approved defines the output size and the black frame.
      const draftBlob = await ensureAsset(file, upscaled ? 'draftUpscaled' : 'draft');
      const draftCanvas = draftBlob ? await blobToCanvas(draftBlob) : null;
      const geometry = resolveOutputGeometry(draftCanvas, bg, file.sku);

      let workCanvas: HTMLCanvasElement;
      let transparent: boolean;
      let source: string;
      let alignment: Record<string, unknown> | null = null;
      const editedMaster = localEdit ? await getStoredBlob(file.id, 'editedMaster') : null;
      if (editedMaster) {
        // Re-edit continues from the full-resolution edited object, not the resized output.
        workCanvas = await blobToCanvas(editedMaster);
        transparent = true;
        source = 'edited_master';
      } else {
        // Edit the FULL-RESOLUTION background-removed object from the server; the final
        // file is re-composed from it on save, so edits never resample target pixels twice.
        const objectBlob = await ensureAsset(file, upscaled ? 'objectUpscaled' : 'object');
        if (objectBlob) {
          const aligned = alignObjectToDraft(await blobToCanvas(objectBlob), geometry.inner);
          await capEditingMaster(aligned.canvas, geometry.targetLongPx);
          workCanvas = aligned.canvas;
          transparent = true;
          source = 'background_removed';
          alignment = { angle: aligned.angle, matched: aligned.matched, aspectDiff: aligned.aspectDiff };
          if (aligned.matched === false) {
            setEditNotice('The background-removed file is framed differently from the draft. Check the rotation/crop before saving.');
          }
        } else {
          const editedFinal = localEdit ? await getStoredBlob(file.id, 'final') : null;
          if (!editedFinal && !draftCanvas) throw new Error('Nothing to edit: the draft and background-removed files are not available');
          workCanvas = editedFinal ? await blobToCanvas(editedFinal) : draftCanvas!;
          // A transparent draft is the object itself; white/black drafts are edited as flat images.
          transparent = bg === 'transparent';
          source = editedFinal ? 'edited_final' : 'draft';
          setEditNotice('No background-removed file for this item — editing the print file itself.');
        }
      }

      if (editedElsewhere) {
        setEditNotice('This file was edited in another browser. Editing here starts again from the unedited file and replaces that edit.');
      }
      editGeometryRef.current = geometry;
      editSourceRef.current = source;
      editOpsRef.current = { crop: 0, brush: 0, rotate: 0, undo: 0, redo: 0 };
      setEditIsTransparent(transparent);
      editIsTransparentRef.current = transparent;
      // Preview the object on the variant's real background color by default.
      setReviewPreviewBg(bg);
      editWorkingCanvasRef.current = workCanvas;
      // Initialize history with the original state
      editHistoryRef.current = [];
      editHistoryIdxRef.current = -1;
      setEditMode(true);
      setEditTool(null);
      setEditHasChanges(false);
      setCanUndo(false);
      setCanRedo(false);
      setCropApplied(false);
      setRotateApplied(false);
      setEditRotationAngle(0);
      cropRectRef.current = null;
      setCropRectUI(null);
      cropDragModeRef.current = null;
      cropDragOriginRef.current = null;
      setEditZoom(1);
      setEditPan({ x: 0, y: 0 });
      setEditCursorStyle('default');
      rotateMouseRef.current = null;
      invalidateRotateBase();
      // Push initial (unmodified) state as history[0]
      pushEditHistory();
      requestAnimationFrame(() => {
        const displayCanvas = editCanvasRef.current;
        if (displayCanvas && editWorkingCanvasRef.current) {
          displayCanvas.width = editWorkingCanvasRef.current.width;
          displayCanvas.height = editWorkingCanvasRef.current.height;
          displayCanvas.getContext('2d')!.drawImage(editWorkingCanvasRef.current, 0, 0);
        }
      });
      logEdit('edit_open', {
        source,
        variant,
        master: { w: workCanvas.width, h: workCanvas.height },
        draft: draftCanvas ? { w: draftCanvas.width, h: draftCanvas.height } : null,
        bg,
        border: geometry.border,
        targetLongPx: geometry.targetLongPx,
        sizeFrom: geometry.source,
        alignment,
      });
    } catch (e: any) {
      setEditError(e?.message || 'Could not open the editor');
      logEdit('edit_open_error', { message: e?.message });
    } finally {
      setEditPreparing(false);
    }
  };

  const cancelEditMode = () => {
    if (editMode) logEdit('edit_cancel', { hadChanges: editHasChanges, ops: { ...editOpsRef.current } });
    setEditMode(false);
    setEditNotice(null);
    // Back in review the preview background always defaults to the checkerboard.
    setReviewPreviewBg('transparent');
    editWorkingCanvasRef.current = null;
    resetEditState();
  };

  const setEditToolSafe = (selected: 'crop' | 'brush' | 'rotate' | null) => {
    // Clicking the active tool again turns it OFF → back to the pan hand.
    const tool = selected !== null && selected === editTool ? null : selected;
    // Clean up previous tool state
    if (tool !== 'crop') {
      cropRectRef.current = null;
      setCropRectUI(null);
      cropDragModeRef.current = null;
    }
    if (tool !== 'rotate') {
      setEditRotationAngle(0);
      rotateMouseRef.current = null;
    }
    // Initialize crop rectangle to full image when entering crop mode
    if (tool === 'crop' && editWorkingCanvasRef.current) {
      const wc = editWorkingCanvasRef.current;
      const initRect = { x: 0, y: 0, w: wc.width, h: wc.height };
      cropRectRef.current = initRect;
      setCropRectUI(initRect);
    }
    setEditCursorStyle(tool === 'crop' ? 'crosshair' : 'default');
    setEditTool(tool);
  };

  const applyCrop = () => {
    const cr = cropRectRef.current;
    if (!cr || !editWorkingCanvasRef.current) return;
    const workCanvas = editWorkingCanvasRef.current;
    const ctx = workCanvas.getContext('2d')!;
    const x = Math.max(0, Math.round(cr.x));
    const y = Math.max(0, Math.round(cr.y));
    const w = Math.min(Math.round(cr.w), workCanvas.width - x);
    const h = Math.min(Math.round(cr.h), workCanvas.height - y);
    if (w < 5 || h < 5) return;
    const before = { w: workCanvas.width, h: workCanvas.height };
    // drawImage-based crop keeps semi-transparent edge colors exact
    // (getImageData/putImageData rounds premultiplied alpha).
    const temp = document.createElement('canvas');
    temp.width = w;
    temp.height = h;
    temp.getContext('2d')!.drawImage(workCanvas, x, y, w, h, 0, 0, w, h);
    workCanvas.width = w;
    workCanvas.height = h;
    ctx.drawImage(temp, 0, 0);
    invalidateRotateBase();
    // Reset crop rect to the new (full) canvas size
    cropRectRef.current = { x: 0, y: 0, w, h };
    setCropRectUI({ x: 0, y: 0, w, h });
    setCropApplied(true);
    setEditHasChanges(true);
    pushEditHistory();
    drawEditOverlay();
    editOpsRef.current.crop += 1;
    logEdit('edit_crop', { from: before, rect: { x, y, w, h } });
  };

  const applyEditRotation = () => {
    const workCanvas = editWorkingCanvasRef.current;
    if (!workCanvas || editRotationAngle === 0) return;
    if (!rotateBaseRef.current) {
      const snap = document.createElement('canvas');
      snap.width = workCanvas.width;
      snap.height = workCanvas.height;
      snap.getContext('2d')!.drawImage(workCanvas, 0, 0);
      rotateBaseRef.current = { snap, total: 0 };
    } else {
      // Restore the pre-rotation pixels so the accumulated angle is applied
      // in a single resample instead of stacking one per Apply.
      const s = rotateBaseRef.current.snap;
      workCanvas.width = s.width;
      workCanvas.height = s.height;
      workCanvas.getContext('2d')!.drawImage(s, 0, 0);
    }
    rotateBaseRef.current.total += editRotationAngle;
    rotateCanvas(workCanvas, rotateBaseRef.current.total);
    const bgColor = bgColorFor(currentReview?.color);
    if (editIsTransparentRef.current) {
      // Only the object rotates — trim by alpha; background is re-composed on save.
      trimCanvas(workCanvas);
    } else if (bgColor !== 'transparent') {
      applyBackgroundColor(workCanvas, bgColor);
      trimCanvasByColor(workCanvas, bgColor);
    } else {
      trimCanvas(workCanvas);
    }
    editOpsRef.current.rotate += 1;
    logEdit('edit_rotate', { angle: editRotationAngle, total: rotateBaseRef.current.total, result: { w: workCanvas.width, h: workCanvas.height } });
    setRotateApplied(true);
    setEditHasChanges(true);
    setEditRotationAngle(0);
    setEditZoom(1);
    setEditPan({ x: 0, y: 0 });
    rotateMouseRef.current = null;
    pushEditHistory();
    drawEditOverlay();
  };

  const handleEditSave = async () => {
    const file = currentReview;
    const geometry = editGeometryRef.current;
    if (!editWorkingCanvasRef.current || !file || !geometry) return;
    setEditSaving(true);
    setEditError(null);
    const started = performance.now();
    try {
      const workCanvas = editWorkingCanvasRef.current;
      const bgColor = geometry.bg;
      let finalBlob: Blob;
      let masterBlob: Blob | null = null;
      let width: number;
      let height: number;
      if (editIsTransparentRef.current) {
        // Object flow: trim the edited full-res master, keep it as the new
        // master, then produce the target-size output with ONE downscale and
        // re-compose the final file (background + border) from scratch.
        trimCanvas(workCanvas);
        masterBlob = await canvasToBlob(workCanvas, 'image/png');
        const outCanvas = cloneCanvas(workCanvas);
        await resizeCanvas(outCanvas, pxToResizeInches(geometry.targetLongPx), 'transparent');
        const composed = await composeFinalBlob(outCanvas, bgColor, geometry.border);
        finalBlob = composed.blob;
        width = composed.width;
        height = composed.height;
      } else {
        // Flat print file (no background-removed object available).
        if (cropApplied || rotateApplied) {
          if (bgColor !== 'transparent') {
            trimCanvasByColor(workCanvas, bgColor);
          } else {
            trimCanvas(workCanvas);
          }
          await resizeCanvas(workCanvas, pxToResizeInches(geometry.targetLongPx), geometry.border ? bgColor : 'transparent');
          if (bgColor !== 'transparent') {
            applyBackgroundColor(workCanvas, bgColor);
          }
        }
        const format = bgColor !== 'transparent' ? 'image/jpeg' : 'image/png';
        // Quality 1: every re-save at lower quality visibly degrades print files.
        finalBlob = await canvasToBlob(workCanvas, format, 1);
        width = workCanvas.width;
        height = workCanvas.height;
      }
      await saveFileEdit(file, { finalBlob, masterBlob, width, height }, {
        source: editSourceRef.current,
        ops: { ...editOpsRef.current },
        master: masterBlob ? { w: workCanvas.width, h: workCanvas.height } : null,
        final: { w: width, h: height },
        targetLongPx: geometry.targetLongPx,
        border: geometry.border,
        type: finalBlob.type,
        bytes: finalBlob.size,
        ms: Math.round(performance.now() - started),
      }, editVariantRef.current);
      setEditMode(false);
      setEditNotice(null);
      editWorkingCanvasRef.current = null;
      resetEditState();
      // Back in review the preview background always defaults to the checkerboard.
      setReviewPreviewBg('transparent');
    } catch (e: any) {
      setEditError(e?.message || 'Saving the edit failed');
      logEdit('edit_save_error', { message: e?.message });
    } finally {
      setEditSaving(false);
    }
  };

  const startPan = (e: React.MouseEvent) => {
    isPanningRef.current = true;
    panOriginRef.current = { mx: e.clientX, my: e.clientY, px: editPan.x, py: editPan.y };
  };

  const handleEditCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();

    // Space+click, Alt+click, middle mouse = always pan
    if (isSpaceHeldRef.current || e.altKey || e.button === 1) {
      startPan(e);
      return;
    }
    // No tool selected + zoomed in = pan by default
    if (!editTool && editZoom > 1) {
      startPan(e);
      return;
    }

    const coords = getCanvasCoords(e);

    if (editTool === 'crop') {
      const cr = cropRectRef.current;
      if (!cr) return;
      const cw = editWorkingCanvasRef.current?.width ?? 1000;
      const zone = getCropHitZone(coords.x, coords.y, cr, cw);
      if (zone) {
        cropDragModeRef.current = zone;
        cropDragOriginRef.current = { mouseX: coords.x, mouseY: coords.y, rect: { ...cr } };
      }
    } else if (editTool === 'brush') {
      isDrawingRef.current = true;
      const workCanvas = editWorkingCanvasRef.current;
      const displayCanvas = editCanvasRef.current;
      if (!workCanvas || !displayCanvas) return;
      const rect = displayCanvas.getBoundingClientRect();
      const scale = workCanvas.width / rect.width;
      brushStrokeSizeRef.current = editBrushSize * scale;
      // Snapshot the pre-stroke state; the live stroke re-composites onto it.
      const backup = document.createElement('canvas');
      backup.width = workCanvas.width;
      backup.height = workCanvas.height;
      backup.getContext('2d')!.drawImage(workCanvas, 0, 0);
      brushBackupRef.current = backup;
      brushPointsRef.current = [coords];
      invalidateRotateBase();
      renderBrushStroke();
      setEditHasChanges(true);
      drawEditOverlay();
    } else if (editTool === 'rotate') {
      const canvas = editCanvasRef.current;
      if (!canvas) return;
      const r = canvas.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      rotateMouseRef.current = {
        cx,
        cy,
        startMouseAngle: Math.atan2(e.clientY - cy, e.clientX - cx),
        startAngle: editRotationAngle,
      };
    }
  };

  const handleEditCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    // Pan
    if (isPanningRef.current && panOriginRef.current) {
      const dx = e.clientX - panOriginRef.current.mx;
      const dy = e.clientY - panOriginRef.current.my;
      setEditPan({ x: panOriginRef.current.px + dx, y: panOriginRef.current.py + dy });
      return;
    }

    const coords = getCanvasCoords(e);

    if (editTool === 'crop') {
      const cr = cropRectRef.current;
      if (!cr) return;
      const cw = editWorkingCanvasRef.current?.width ?? 1;
      const ch = editWorkingCanvasRef.current?.height ?? 1;

      if (cropDragModeRef.current && cropDragOriginRef.current) {
        const dx = coords.x - cropDragOriginRef.current.mouseX;
        const dy = coords.y - cropDragOriginRef.current.mouseY;
        const orig = cropDragOriginRef.current.rect;
        let nx = orig.x, ny = orig.y, nw = orig.w, nh = orig.h;
        const mode = cropDragModeRef.current;

        if (mode === 'move') {
          nx = Math.max(0, Math.min(cw - orig.w, orig.x + dx));
          ny = Math.max(0, Math.min(ch - orig.h, orig.y + dy));
        } else {
          if (mode.includes('w')) { nx = Math.max(0, Math.min(orig.x + orig.w - 20, orig.x + dx)); nw = orig.w - (nx - orig.x); }
          if (mode.includes('e')) { nw = Math.max(20, Math.min(cw - orig.x, orig.w + dx)); }
          if (mode.includes('n')) { ny = Math.max(0, Math.min(orig.y + orig.h - 20, orig.y + dy)); nh = orig.h - (ny - orig.y); }
          if (mode.includes('s')) { nh = Math.max(20, Math.min(ch - orig.y, orig.h + dy)); }
        }

        cropRectRef.current = { x: nx, y: ny, w: nw, h: nh };
        drawEditOverlay();
      } else {
        // Hover — update cursor to indicate what can be dragged
        const zone = getCropHitZone(coords.x, coords.y, cr, cw);
        setEditCursorStyle(getCropCursorStyle(zone));
      }
    } else if (editTool === 'brush') {
      if (isDrawingRef.current) {
        if (!brushPointsRef.current.length) return;
        brushPointsRef.current.push(coords);
        renderBrushStroke();
        drawEditOverlay();
      }
    } else if (editTool === 'rotate' && rotateMouseRef.current) {
      // The object rotates to follow the cursor around the canvas center
      const rm = rotateMouseRef.current;
      const cur = Math.atan2(e.clientY - rm.cy, e.clientX - rm.cx);
      let delta = ((cur - rm.startMouseAngle) * 180) / Math.PI;
      if (delta > 180) delta -= 360;
      if (delta < -180) delta += 360;
      setEditRotationAngle(Math.round((rm.startAngle + delta) * 10) / 10);
    }
  };

  const handleEditCanvasMouseUp = () => {
    if (isPanningRef.current) {
      isPanningRef.current = false;
      panOriginRef.current = null;
      return;
    }
    if (editTool === 'crop' && cropDragModeRef.current) {
      cropDragModeRef.current = null;
      cropDragOriginRef.current = null;
      setCropRectUI(cropRectRef.current ? { ...cropRectRef.current } : null);
    }
    if (editTool === 'brush' && isDrawingRef.current) {
      isDrawingRef.current = false;
      endBrushStroke();
      drawEditOverlay();
    }
    if (editTool === 'rotate') {
      rotateMouseRef.current = null;
    }
  };

  // Wheel zoom – zooms toward cursor position
  const handleEditWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const canvas = editCanvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const oldZoom = editZoom;
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.25, Math.min(10, Math.round(oldZoom * factor * 100) / 100));
    if (newZoom === oldZoom) return;

    // Mouse offset from canvas visual center
    const mcx = e.clientX - (rect.left + rect.width / 2);
    const mcy = e.clientY - (rect.top + rect.height / 2);

    // With transform-origin: center, points move proportionally to zoom ratio.
    // Compensate pan so the point under the cursor stays at the same screen position.
    const ratio = 1 - newZoom / oldZoom;
    setEditZoom(newZoom);
    setEditPan(p => ({
      x: Math.round(p.x + mcx * ratio),
      y: Math.round(p.y + mcy * ratio),
    }));
  };

  // Global mouseup handler for edit mode
  useEffect(() => {
    if (!editMode) return;
    const handleGlobalMouseUp = () => {
      if (isPanningRef.current) {
        isPanningRef.current = false;
        panOriginRef.current = null;
      }
      if (cropDragModeRef.current) {
        cropDragModeRef.current = null;
        cropDragOriginRef.current = null;
        setCropRectUI(cropRectRef.current ? { ...cropRectRef.current } : null);
      }
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        endBrushStrokeRef.current();
        drawOverlayRef.current();
      }
      if (rotateMouseRef.current) {
        rotateMouseRef.current = null;
      }
    };
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => window.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [editMode]);

  const editToolRef = useRef(editTool);
  editToolRef.current = editTool;

  // Keyboard listener: Space for pan, Ctrl+Z / Ctrl+Shift+Z for undo/redo,
  // [ / ] for the brush size (physical keys, so any keyboard layout works)
  useEffect(() => {
    if (!editMode) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        isSpaceHeldRef.current = true;
        setIsSpaceHeld(true);
      }
      if ((e.code === 'BracketLeft' || e.code === 'BracketRight') && !e.ctrlKey && !e.metaKey && !e.altKey && editToolRef.current === 'brush') {
        const target = e.target as HTMLElement | null;
        const typing = !!target && (target.tagName === 'TEXTAREA' || target.isContentEditable
          || (target.tagName === 'INPUT' && !['range', 'checkbox', 'radio', 'button'].includes((target as HTMLInputElement).type)));
        if (!typing) {
          e.preventDefault();
          const dir = e.code === 'BracketRight' ? 1 : -1;
          setEditBrushSize((s) => {
            // Like Photoshop: 1px steps for small brushes, coarser above 10px
            const step = (dir > 0 ? s : s - 1) < 10 ? 1 : 5;
            return Math.min(BRUSH_SIZE_MAX, Math.max(BRUSH_SIZE_MIN, s + dir * step));
          });
        }
      }
      // Ctrl+Z = undo, Ctrl+Shift+Z / Ctrl+Y = redo
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
        e.preventDefault();
        if (e.shiftKey) {
          editRedoRef.current();
        } else {
          editUndoRef.current();
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyY') {
        e.preventDefault();
        editRedoRef.current();
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        isSpaceHeldRef.current = false;
        setIsSpaceHeld(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      isSpaceHeldRef.current = false;
      setIsSpaceHeld(false);
    };
  }, [editMode]);

  const recentLogs = useMemo(() => fileLogs.slice(-40).reverse(), [fileLogs]);

  const serverPack = usePackDetails(batch.id, showServerHistory);
  const serverHistory = currentReview
    ? serverPack.details?.printFiles.find((f) => f.printReadyFileId === currentReview.id)?.history ?? null
    : null;

  if (!files.length) {
    return (
      <div className="fixed inset-0 bg-black/90 z-[1400] flex items-center justify-center">
        <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 text-center text-gray-300">
          <p className="mb-4">Set #{batch.id} has no files on this device.</p>
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white border-0">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 z-[1400] flex flex-row-reverse">
      {/* Image area (right) — square, keeps full height */}
      <div className="flex-1 flex items-center justify-center p-3 min-w-0">
        <div className="h-full w-full bg-slate-900/50 rounded-xl border border-slate-700 p-2 flex flex-col relative overflow-hidden">
          {editMode ? (
            <>
              {/* Edit Canvas with Overlay Controls */}
              <div
                ref={editContainerRef}
                onMouseDown={(e) => {
                  // Rotate can be grabbed anywhere in the viewport, not just on the canvas
                  if (editTool === 'rotate' && e.target === editContainerRef.current) {
                    e.preventDefault();
                    const canvas = editCanvasRef.current;
                    if (!canvas) return;
                    const r = canvas.getBoundingClientRect();
                    const cx = r.left + r.width / 2;
                    const cy = r.top + r.height / 2;
                    rotateMouseRef.current = {
                      cx,
                      cy,
                      startMouseAngle: Math.atan2(e.clientY - cy, e.clientX - cx),
                      startAngle: editRotationAngle,
                    };
                    return;
                  }
                  // Click on empty container area (not canvas) – start pan
                  if (e.target === editContainerRef.current && editZoom > 1) {
                    e.preventDefault();
                    startPan(e);
                  }
                }}
                onMouseMove={(e) => {
                  if (isPanningRef.current && panOriginRef.current) {
                    const dx = e.clientX - panOriginRef.current.mx;
                    const dy = e.clientY - panOriginRef.current.my;
                    setEditPan({ x: panOriginRef.current.px + dx, y: panOriginRef.current.py + dy });
                    return;
                  }
                  if (editTool === 'rotate' && rotateMouseRef.current) {
                    const rm = rotateMouseRef.current;
                    const cur = Math.atan2(e.clientY - rm.cy, e.clientX - rm.cx);
                    let delta = ((cur - rm.startMouseAngle) * 180) / Math.PI;
                    if (delta > 180) delta -= 360;
                    if (delta < -180) delta += 360;
                    setEditRotationAngle(Math.round((rm.startAngle + delta) * 10) / 10);
                  }
                }}
                onMouseUp={() => {
                  if (isPanningRef.current) {
                    isPanningRef.current = false;
                    panOriginRef.current = null;
                  }
                }}
                onWheel={handleEditWheel}
                className="flex-1 flex items-center justify-center overflow-hidden rounded-lg transition-colors duration-200 relative"
                style={{
                  cursor: (isSpaceHeld || (!editTool && editZoom > 1))
                    ? (isPanningRef.current ? 'grabbing' : 'grab')
                    : undefined,
                  backgroundColor: reviewPreviewBg === 'transparent' ? undefined : reviewPreviewBg,
                  backgroundImage: reviewPreviewBg === 'transparent'
                    ? 'linear-gradient(45deg, var(--pfr-checker-canvas, #333) 25%, transparent 25%), linear-gradient(-45deg, var(--pfr-checker-canvas, #333) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--pfr-checker-canvas, #333) 75%), linear-gradient(-45deg, transparent 75%, var(--pfr-checker-canvas, #333) 75%)'
                    : undefined,
                  backgroundSize: reviewPreviewBg === 'transparent' ? '20px 20px' : undefined,
                  backgroundPosition: reviewPreviewBg === 'transparent' ? '0 0, 0 10px, 10px -10px, -10px 0px' : undefined,
                }}
              >
                <canvas
                  ref={editCanvasRef}
                  onMouseDown={handleEditCanvasMouseDown}
                  onMouseMove={handleEditCanvasMouseMove}
                  onMouseUp={handleEditCanvasMouseUp}
                  onWheel={handleEditWheel}
                  className="max-w-full max-h-full block"
                  style={{
                    cursor: isSpaceHeld
                      ? (isPanningRef.current ? 'grabbing' : 'grab')
                      : editTool === 'brush'
                        ? brushCursorCss
                        : editTool === 'crop'
                          ? editCursorStyle
                          : editTool === 'rotate'
                            ? (rotateMouseRef.current ? 'grabbing' : 'grab')
                            : editZoom > 1
                              ? 'grab'
                              : 'default',
                    transform: [
                      editPan.x || editPan.y ? `translate(${editPan.x}px, ${editPan.y}px)` : '',
                      editZoom * rotateFitScale !== 1 ? `scale(${(editZoom * rotateFitScale).toFixed(4)})` : '',
                      editTool === 'rotate' && editRotationAngle !== 0 ? `rotate(${editRotationAngle}deg)` : '',
                    ].filter(Boolean).join(' ') || 'none',
                    transition: editTool === 'rotate' && !rotateMouseRef.current ? 'transform 0.15s ease' : 'none',
                  }}
                />
                {/* Vertical Toolbar - Left side overlay */}
                <div className="absolute left-2 top-2 bottom-2 z-20 flex flex-col gap-1.5" style={{ pointerEvents: 'none' }}>
                  {/* Tool Buttons */}
                  <div className="flex flex-col gap-0.5 bg-black/70 backdrop-blur-sm p-1 rounded-lg" style={{ pointerEvents: 'auto' }}>
                    {(['crop', 'brush', 'rotate'] as const).map((tool) => (
                      <button
                        key={tool}
                        type="button"
                        onClick={() => setEditToolSafe(tool)}
                        title={editTool === tool
                          ? `${tool.charAt(0).toUpperCase() + tool.slice(1)} — click again for pan`
                          : tool.charAt(0).toUpperCase() + tool.slice(1)}
                        className={`w-8 h-8 rounded flex items-center justify-center border-0 transition-all ${
                          editTool === tool ? 'bg-pink-600 text-white' : 'text-black-300 hover:text-white hover:bg-white/20'
                        }`}
                      >
                        {tool === 'crop' && <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7.5L4 3.5L8 3.5M16 3.5L20 3.5L20 7.5M20 16.5L20 20.5L16 20.5M8 20.5L4 20.5L4 16.5" /></svg>}
                        {tool === 'brush' && <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>}
                        {tool === 'rotate' && <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>}
                      </button>
                    ))}
                  </div>
                  {/* Undo / Redo */}
                  <div className="flex flex-col gap-0.5 bg-black/70 backdrop-blur-sm p-1 rounded-lg" style={{ pointerEvents: 'auto' }}>
                    <button
                      type="button"
                      onClick={editUndo}
                      disabled={!canUndo}
                      title="Undo (Ctrl+Z)"
                      className="w-8 h-8 rounded flex items-center justify-center text-black-300 hover:text-white hover:bg-white/20 transition-all border-0 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a5 5 0 015 5v2M3 10l4-4M3 10l4 4" /></svg>
                    </button>
                    <button
                      type="button"
                      onClick={editRedo}
                      disabled={!canRedo}
                      title="Redo (Ctrl+Shift+Z)"
                      className="w-8 h-8 rounded flex items-center justify-center text-black-300 hover:text-white hover:bg-white/20 transition-all border-0 disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10H11a5 5 0 00-5 5v2M21 10l-4-4M21 10l-4 4" /></svg>
                    </button>
                  </div>
                  {/* Background Switcher */}
                  <div className="flex flex-col gap-0.5 bg-black/70 backdrop-blur-sm p-1 rounded-lg" style={{ pointerEvents: 'auto' }}>
                    <button
                      type="button"
                      onClick={() => setReviewPreviewBg('transparent')}
                      title="Transparent"
                      className={`w-8 h-8 rounded flex items-center justify-center border-0 transition-all ${
                        reviewPreviewBg === 'transparent' ? 'ring-2 ring-pink-500' : 'hover:ring-1 hover:ring-white/30'
                      }`}
                    >
                      <div className="w-5 h-5 rounded-sm" style={{
                        backgroundImage: 'linear-gradient(45deg, var(--pfr-checker, #666) 25%, transparent 25%), linear-gradient(-45deg, var(--pfr-checker, #666) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--pfr-checker, #666) 75%), linear-gradient(-45deg, transparent 75%, var(--pfr-checker, #666) 75%)',
                        backgroundSize: '8px 8px',
                        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                      }} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setReviewPreviewBg('white')}
                      title="White"
                      className={`w-8 h-8 rounded flex items-center justify-center border-0 transition-all ${
                        reviewPreviewBg === 'white' ? 'ring-2 ring-pink-500' : 'hover:ring-1 hover:ring-white/30'
                      }`}
                    >
                      <div className="w-5 h-5 rounded-sm bg-white" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setReviewPreviewBg('black')}
                      title="Black"
                      className={`w-8 h-8 rounded flex items-center justify-center border-0 transition-all ${
                        reviewPreviewBg === 'black' ? 'ring-2 ring-pink-500' : 'hover:ring-1 hover:ring-white/30'
                      }`}
                    >
                      <div className="w-5 h-5 rounded-sm bg-black border border-gray-600" />
                    </button>
                  </div>
                  {/* Zoom Controls */}
                  <div className="flex flex-col gap-0.5 bg-black/70 backdrop-blur-sm p-1 rounded-lg" style={{ pointerEvents: 'auto' }}>
                    <button
                      type="button"
                      onClick={() => setEditZoom(z => Math.min(10, Math.round(z * 1.25 * 100) / 100))}
                      title="Zoom In"
                      className="w-8 h-8 rounded flex items-center justify-center text-black-300 hover:text-white hover:bg-white/20 transition-all border-0 text-sm font-bold"
                    >+</button>
                    <div className="text-[9px] text-gray-400 text-center leading-tight select-none">{Math.round(editZoom * 100)}%</div>
                    <button
                      type="button"
                      onClick={() => setEditZoom(z => Math.max(0.25, Math.round(z * 0.8 * 100) / 100))}
                      title="Zoom Out"
                      className="w-8 h-8 rounded flex items-center justify-center text-black-300 hover:text-white hover:bg-white/20 transition-all border-0 text-sm font-bold"
                    >&minus;</button>
                    <button
                      type="button"
                      onClick={() => { setEditZoom(1); setEditPan({ x: 0, y: 0 }); }}
                      title="Reset Zoom &amp; Pan"
                      className="w-8 h-8 rounded flex items-center justify-center text-black-300 hover:text-white hover:bg-white/20 transition-all border-0 text-[9px] font-medium"
                    >1:1</button>
                    {editZoom > 1 && (
                      <div className="text-[8px] text-gray-500 text-center leading-tight select-none mt-0.5">
                        Space<br/>+drag
                      </div>
                    )}
                  </div>
                  {/* Spacer */}
                  <div className="flex-1" />
                  {/* Cancel & Save */}
                  <div className="flex flex-col gap-0.5 bg-black/70 backdrop-blur-sm p-1 rounded-lg" style={{ pointerEvents: 'auto' }}>
                    <button
                      type="button"
                      onClick={cancelEditMode}
                      title="Cancel"
                      className="w-8 h-8 rounded flex items-center justify-center text-black-300 hover:text-white hover:bg-white/20 transition-all border-0"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                    {editHasChanges && (
                      <button
                        type="button"
                        onClick={handleEditSave}
                        disabled={editSaving}
                        title="Save"
                        className="w-8 h-8 rounded flex items-center justify-center bg-green-600 hover:bg-green-700 text-white transition-all border-0 disabled:opacity-50"
                      >
                        {editSaving ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {/* Tool-specific Options - Floating panel */}
                {editTool === 'brush' && (
                  <div className="absolute top-2 left-14 z-20 flex items-center gap-3 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2">
                    {editIsTransparent ? (
                      <span className="text-[10px] text-pink-400 uppercase tracking-wide font-bold">Eraser</span>
                    ) : (
                      <>
                        <span className="text-[10px] text-gray-400 uppercase tracking-wide">Color:</span>
                        <button
                          type="button"
                          onClick={() => setEditBrushColor('white')}
                          className={`w-6 h-6 rounded-full bg-white border-2 transition-all ${
                            editBrushColor === 'white' ? 'border-pink-500 scale-110' : 'border-gray-600'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setEditBrushColor('black')}
                          className={`w-6 h-6 rounded-full bg-black border-2 transition-all ${
                            editBrushColor === 'black' ? 'border-pink-500 scale-110' : 'border-gray-600'
                          }`}
                        />
                      </>
                    )}
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide ml-2">Size:</span>
                    <input
                      type="range"
                      min={BRUSH_SIZE_MIN}
                      max={BRUSH_SIZE_MAX}
                      value={editBrushSize}
                      onChange={(e) => setEditBrushSize(parseInt(e.target.value, 10))}
                      className="w-24 accent-pink-500"
                      title="[ smaller · ] larger"
                    />
                    <span className="text-xs text-white font-mono w-8">{editBrushSize}px</span>
                    <span className="text-[10px] text-gray-500 font-mono" title="Keyboard: [ smaller, ] larger">[ ]</span>
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide ml-2">Hardness:</span>
                    <input
                      type="range"
                      min={50}
                      max={100}
                      value={editBrushHardness}
                      onChange={(e) => setEditBrushHardness(parseInt(e.target.value, 10))}
                      className="w-20 accent-pink-500"
                    />
                    <span className="text-xs text-white font-mono w-9">{editBrushHardness}%</span>
                  </div>
                )}
                {editTool === 'rotate' && (
                  <div className="absolute top-2 left-14 z-20 flex flex-col gap-1.5 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex gap-1">
                        {[-90, -5, -1].map((v) => (
                          <button key={v} type="button" onClick={() => setEditRotationAngle((a) => a + v)} className="px-2 py-1 rounded text-[10px] bg-slate-700 hover:bg-slate-600 text-white border-0 transition-colors">{v}&deg;</button>
                        ))}
                      </div>
                      <input
                        type="number"
                        value={editRotationAngle}
                        onChange={(e) => setEditRotationAngle(parseFloat(e.target.value) || 0)}
                        className="w-16 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white text-center focus:outline-none focus:border-pink-500"
                        step="0.1"
                      />
                      <div className="flex gap-1">
                        {[1, 5, 90].map((v) => (
                          <button key={v} type="button" onClick={() => setEditRotationAngle((a) => a + v)} className="px-2 py-1 rounded text-[10px] bg-slate-700 hover:bg-slate-600 text-white border-0 transition-colors">+{v}&deg;</button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={applyEditRotation}
                        disabled={editRotationAngle === 0}
                        className="px-3 py-1 rounded text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white border-0 transition-colors disabled:opacity-50 ml-1"
                      >
                        Apply
                      </button>
                    </div>
                    <span className="text-[10px] text-gray-400 italic">Drag around the image center — the object follows the cursor</span>
                  </div>
                )}
                {editTool === 'crop' && cropRectUI && (
                  <div className="absolute top-2 left-14 z-20 flex items-center gap-2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2">
                    <span className="text-xs text-gray-300 font-mono">
                      {Math.round(cropRectUI.w)} &times; {Math.round(cropRectUI.h)}
                    </span>
                    <button
                      type="button"
                      onClick={applyCrop}
                      className="px-3 py-1 rounded text-xs font-bold bg-blue-600 hover:bg-blue-700 text-white border-0 transition-colors"
                    >
                      Apply Crop
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const wc = editWorkingCanvasRef.current;
                        if (wc) {
                          const full = { x: 0, y: 0, w: wc.width, h: wc.height };
                          cropRectRef.current = full;
                          setCropRectUI(full);
                          drawEditOverlay();
                        }
                      }}
                      className="px-3 py-1 rounded text-xs text-black-300 hover:text-white hover:bg-white/10 border-0 transition-colors"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
          <div
            ref={reviewImgWrapRef}
            onMouseMove={handleLoupeMove}
            onMouseLeave={() => setLoupePos(null)}
            className="flex-1 flex items-center justify-center overflow-hidden rounded-lg transition-colors duration-200 relative"
            style={{
              backgroundColor: reviewPreviewBg === 'transparent' ? undefined : reviewPreviewBg,
              backgroundImage: reviewPreviewBg === 'transparent'
                ? 'linear-gradient(45deg, var(--pfr-checker-canvas, #333) 25%, transparent 25%), linear-gradient(-45deg, var(--pfr-checker-canvas, #333) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--pfr-checker-canvas, #333) 75%), linear-gradient(-45deg, transparent 75%, var(--pfr-checker-canvas, #333) 75%)'
                : undefined,
              backgroundSize: reviewPreviewBg === 'transparent' ? '20px 20px' : undefined,
              backgroundPosition: reviewPreviewBg === 'transparent' ? '0 0, 0 10px, 10px -10px, -10px 0px' : undefined,
              cursor: loupeEnabled ? 'zoom-in' : undefined,
            }}
          >
            {reviewResultSrc ? (
              <img
                ref={reviewImgRef}
                src={reviewResultSrc}
                alt="Result"
                onLoad={(e) => setReviewNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                className="max-w-full max-h-full object-contain transition-transform duration-300"
              />
            ) : (
              <div className="text-sm text-gray-400 bg-black/60 px-3 py-2 rounded">
                {currentReview?.assets.draft.state === 'missing' ? 'No draft print file from the server' : 'Loading print file…'}
              </div>
            )}
            {/* Loupe: 200% of actual print-ready pixels (like Adobe Bridge, but bigger) */}
            {loupeEnabled && loupePos && reviewNatural && reviewResultSrc && (
              <div
                className="pointer-events-none absolute z-30 rounded-lg border-2 border-pink-500/80 shadow-2xl shadow-black/60 overflow-hidden"
                style={{
                  width: LOUPE_SIZE,
                  height: LOUPE_SIZE,
                  left: loupePos.x,
                  top: loupePos.y,
                  backgroundColor: reviewPreviewBg === 'black' ? '#000' : '#fff',
                  backgroundImage: `url(${reviewResultSrc})`,
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: `${reviewNatural.w * LOUPE_ZOOM}px ${reviewNatural.h * LOUPE_ZOOM}px`,
                  backgroundPosition: `${LOUPE_SIZE / 2 - loupePos.imgX * LOUPE_ZOOM}px ${LOUPE_SIZE / 2 - loupePos.imgY * LOUPE_ZOOM}px`,
                  imageRendering: 'pixelated',
                }}
              >
                <div className="absolute bottom-1 right-2 text-[10px] font-semibold text-pink-400 bg-black/60 px-1.5 rounded">200%</div>
              </div>
            )}
            {reviewResultSrc && (
              <button
                type="button"
                onClick={() => void handleDownloadCurrent()}
                onMouseMove={(e) => e.stopPropagation()}
                onMouseEnter={() => setLoupePos(null)}
                disabled={downloading}
                title="Download this print-ready file"
                className="absolute bottom-3 right-3 z-30 w-9 h-9 rounded-full flex items-center justify-center p-0 border-0 bg-black/60 backdrop-blur-sm text-white hover:bg-pink-600 transition-colors disabled:opacity-60 cursor-pointer"
              >
                {downloading ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
              </button>
            )}
            {editPreparing && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 z-40 gap-3">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500" />
                <span className="text-xs text-gray-300">Preparing full-resolution file…</span>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Data panel (left) - all controls and info */}
      <div className="w-[560px] shrink-0 bg-slate-900/80 border-r border-slate-700 flex flex-col p-3 gap-3 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-lg font-bold text-white">Set #{batch.id} Review</h2>
            <p className="text-gray-400 text-xs">
              Image {currentReviewIndex + 1} of {files.length}
              <span className="mx-1.5">·</span>
              Decided {decidedCount}/{files.length}
              <span className="mx-1.5">·</span>
              <span className={syncErrorCount ? 'text-red-400' : syncedCount === decidedCount ? 'text-green-400' : 'text-blue-300'}>
                Saved {syncedCount}/{decidedCount}{syncErrorCount ? ` · ${syncErrorCount} failed` : ''}
              </span>
            </p>
            {(syncErrorCount > 0 || retriesStopped) && (
              <div className="flex items-center gap-1.5 mt-1 text-[11px]">
                {retriesStopped && <span className="text-amber-300">Auto-retry stopped</span>}
                {syncErrorCount > 0 && (
                  <button type="button" onClick={() => void retryAllNow()} className="px-2 py-0.5 rounded bg-slate-700 hover:bg-slate-600 text-white border-0">
                    Retry now
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => void setRetriesStopped(!retriesStopped)}
                  title={retriesStopped ? 'Failed saves are retried automatically again' : 'Stop retrying failed saves automatically — they wait for Retry now'}
                  className={`px-2 py-0.5 rounded border-0 text-white ${retriesStopped ? 'bg-amber-600/80 hover:bg-amber-600' : 'bg-slate-800 hover:bg-slate-700'}`}
                >
                  {retriesStopped ? 'Resume retries' : 'Stop retries'}
                </button>
              </div>
            )}
            {nextBatch && !batch.viewOnly && (
              <p className="text-[11px] text-gray-500">
                Next set #{nextBatch.id}: {nextProgress.complete ? 'ready' : `downloading ${nextProgress.done}/${nextProgress.total} images`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {readOnly && (
              <span
                className="px-2.5 py-1 rounded-lg bg-green-600/20 text-green-300 text-xs font-medium"
                title={batch.viewOnly ? 'Loaded from the server for viewing — it can’t be changed here' : 'This set is done — it can only be viewed'}
              >
                {batch.viewOnly ? `View only${batch.createdBy ? ` · ${batch.createdBy}` : ''}` : 'View only · set done'}
              </span>
            )}
            {!editMode && !readOnly && (
              <button
                type="button"
                onClick={enterEditMode}
                disabled={editPreparing || !currentReview}
                className="px-3 py-1.5 bg-amber-600/80 hover:bg-amber-600 rounded-lg text-white text-xs font-medium flex items-center gap-1.5 transition-colors border-0 disabled:opacity-60"
              >
                {editPreparing ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                )}
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={() => { if (editMode) cancelEditMode(); onClose(); }}
              className="w-8 h-8 flex items-center justify-center p-0 hover:bg-white/10 rounded-full transition-colors shrink-0 border-0"
            >
              <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {(editNotice || editError) && (
          <div className={`rounded-lg px-2.5 py-1.5 text-[12px] border ${editError ? 'bg-red-500/10 border-red-500/30 text-red-200' : 'bg-amber-500/10 border-amber-500/30 text-amber-100'}`}>
            {editError || editNotice}
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handlePrev}
            disabled={editMode || currentReviewIndex === 0}
            className="flex-1 py-2 flex items-center justify-center gap-1 bg-slate-800/50 hover:bg-slate-700 rounded-lg text-white text-xs disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-0"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={editMode || currentReviewIndex === files.length - 1}
            className="flex-1 py-2 flex items-center justify-center gap-1 bg-slate-800/50 hover:bg-slate-700 rounded-lg text-white text-xs disabled:opacity-30 disabled:cursor-not-allowed transition-colors border-0"
          >
            Next
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* View options (moved off the image so nothing covers it) */}
        {!editMode && (
          <div className="bg-slate-800/30 rounded-xl border border-white/5 p-2 flex items-center gap-2 flex-wrap">
            <div className="bg-black/40 p-0.5 rounded-lg flex gap-0.5">
              <button
                type="button"
                onClick={() => setReviewPreviewBg('transparent')}
                className={`w-6 h-6 rounded flex items-center justify-center border-0 transition-all ${
                  reviewPreviewBg === 'transparent' ? 'ring-2 ring-pink-500' : 'hover:ring-1 hover:ring-white/30'
                }`}
                title="Transparent background"
              >
                <div className="w-4 h-4 rounded-sm" style={{
                  backgroundImage: 'linear-gradient(45deg, var(--pfr-checker, #666) 25%, transparent 25%), linear-gradient(-45deg, var(--pfr-checker, #666) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--pfr-checker, #666) 75%), linear-gradient(-45deg, transparent 75%, var(--pfr-checker, #666) 75%)',
                  backgroundSize: '8px 8px',
                  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                }} />
              </button>
              <button
                type="button"
                onClick={() => setReviewPreviewBg('white')}
                className={`w-6 h-6 rounded flex items-center justify-center border-0 transition-all ${
                  reviewPreviewBg === 'white' ? 'ring-2 ring-pink-500' : 'hover:ring-1 hover:ring-white/30'
                }`}
                title="White background"
              >
                <div className="w-4 h-4 rounded-sm bg-white" />
              </button>
              <button
                type="button"
                onClick={() => setReviewPreviewBg('black')}
                className={`w-6 h-6 rounded flex items-center justify-center border-0 transition-all ${
                  reviewPreviewBg === 'black' ? 'ring-2 ring-pink-500' : 'hover:ring-1 hover:ring-white/30'
                }`}
                title="Black background"
              >
                <div className="w-4 h-4 rounded-sm bg-black border border-gray-600" />
              </button>
            </div>
            {currentReview && hasUpscaled(currentReview) && !readOnly && (
              <div className="bg-black/40 p-0.5 rounded-lg flex gap-0.5" title="The decision is saved for the version on screen">
                {(['regular', 'upscaled'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setViewVariants((m) => ({ ...m, [currentReview.id]: v }))}
                    className={`px-2 py-1 rounded text-[11px] font-medium transition-all border-0 ${
                      currentVariant === v ? 'bg-pink-600 text-white' : 'bg-transparent text-gray-300 hover:text-white'
                    }`}
                  >
                    {v === 'regular' ? 'Regular' : 'Upscaled (AI)'}
                  </button>
                ))}
              </div>
            )}
            <label className="flex items-center gap-1.5 cursor-pointer bg-black/40 px-2 py-1.5 rounded hover:bg-black/60 transition-colors">
              <input
                type="checkbox"
                checked={loupeEnabled}
                onChange={(e) => setLoupeEnabled(e.target.checked)}
                className="w-3 h-3 rounded border-gray-500 text-pink-600 focus:ring-offset-0 focus:ring-pink-500"
              />
              <span className="text-[11px] text-gray-300 select-none">Loupe 200%</span>
            </label>
          </div>
        )}

        {/* Original — intentionally larger than the mockup */}
        <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-2 flex flex-col relative group/original">
          <div className="text-xs text-gray-400 absolute top-2 left-2 bg-black/60 px-2 py-0.5 rounded z-10">
            Original
          </div>
          <div className="flex items-center justify-center h-[230px] overflow-hidden relative">
            {reviewOriginalSrc ? (
              <img
                src={reviewOriginalSrc}
                alt="Original"
                className="max-w-full max-h-full object-contain cursor-zoom-in"
              />
            ) : (
              currentReview?.assets.original.state === 'missing' && <div className="text-xs text-gray-500 italic">No Original</div>
            )}
          </div>
          {/* Hover preview — full viewport height, to the right of the panel */}
          {reviewOriginalSrc && (
            <div className="hidden group-hover/original:flex fixed inset-y-0 left-[576px] z-[9999] max-w-[calc(100vw-592px)]">
              <div className="relative h-full bg-slate-900 rounded-2xl border border-slate-600 p-2 shadow-2xl shadow-black/50 flex items-center justify-center">
                <div className="text-xs text-gray-400 absolute top-2 left-2 bg-black/60 px-2 py-0.5 rounded z-10">
                  Original
                </div>
                <img
                  src={reviewOriginalSrc}
                  alt="Original Preview"
                  className="h-full w-auto max-w-full object-contain"
                />
              </div>
            </div>
          )}
        </div>

        {/* Mockup + Print-Ready preview side by side */}
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-2 flex flex-col relative group/mockup">
            <div className="text-xs text-gray-400 absolute top-2 left-2 bg-black/60 px-2 py-0.5 rounded z-10">
              Mockup
            </div>
            <div className="flex items-center justify-center h-[150px] overflow-hidden relative">
              {reviewMockupSrc ? (
                <img
                  src={reviewMockupSrc}
                  alt="Mockup"
                  className="max-w-full max-h-full object-contain cursor-zoom-in"
                />
              ) : (
                currentReview?.assets.mockup.state === 'missing' && <div className="text-xs text-gray-500 italic">No Mockup</div>
              )}
            </div>
            {reviewMockupSrc && (
              <div className="hidden group-hover/mockup:flex fixed top-1/2 left-[576px] -translate-y-1/2 z-[9999]">
                <div className="relative bg-slate-900 rounded-2xl border border-slate-600 p-3 shadow-2xl shadow-black/50">
                  <div className="text-xs text-gray-400 absolute top-2 left-2 bg-black/60 px-2 py-0.5 rounded z-10">
                    Mockup
                  </div>
                  <img
                    src={reviewMockupSrc}
                    alt="Mockup Preview"
                    className="w-[560px] h-[560px] object-contain"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="bg-slate-900/50 rounded-xl border border-slate-700 p-2 flex flex-col relative">
            <div className="text-xs text-gray-400 absolute top-2 left-2 bg-black/60 px-2 py-0.5 rounded z-10">
              Print-Ready{shownLabel}
            </div>
            <div className="flex items-center justify-center h-[150px] overflow-hidden relative canvas-container rounded-lg">
              {reviewResultSrc && (
                <img
                  src={reviewResultSrc}
                  alt="Print-Ready"
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          </div>
        </div>

        {/* Order Information */}
        <div className="bg-slate-800/30 rounded-xl border border-white/5 p-3 flex flex-col gap-2 text-sm">
          <div className="flex items-baseline gap-2">
            <span className="text-gray-400 text-xs shrink-0">Order:</span>
            <span className="text-blue-300 font-semibold break-all select-all">{reviewFilename}</span>
            <span className="text-gray-500 text-[11px] ml-auto select-all">File #{currentReview?.id}</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex items-baseline gap-2">
              <span className="text-gray-400 text-xs">Color:</span>
              <span className="text-white text-[13px]">{reviewColor}</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-gray-400 text-xs">SKU:</span>
              <span className="text-white text-[13px]">{reviewSKU}</span>
            </div>
          </div>
          {reviewCroppingNotes && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-2.5 py-1.5">
              <span className="text-amber-400 text-[10px] uppercase tracking-wide font-semibold">Cropping Notes</span>
              <div className="text-amber-100 break-words">{reviewCroppingNotes}</div>
            </div>
          )}
          <div className="flex items-baseline gap-2">
            <span className="text-gray-400 text-xs">Print Size:</span>
            <span className="text-white font-bold text-base">{formatInches(reviewResultSize)}</span>
            <span className="text-gray-500 text-[10px]">@ 72 DPI</span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-gray-500">
            <span>Orig: {reviewOriginalSize} px</span>
            <span className="mx-1">|</span>
            <span>Res: {reviewResultSize ?? '--'} px</span>
          </div>
          {showingEdit && (
            <div className="flex items-center gap-2 text-[11px]">
              <span className="px-1.5 py-0.5 rounded bg-amber-600/30 text-amber-200 font-semibold">Edited</span>
              <span className="text-gray-400">
                {readOnly ? 'saved on the server'
                  : currentShown.remoteEdit ? 'edited in another browser — already on the server'
                  : 'uploaded to S3 when the decision is saved'}
              </span>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => currentReview && void revertFileEdit(currentReview)}
                  disabled={editMode}
                  className="ml-auto px-2 py-0.5 rounded text-[11px] text-gray-300 hover:text-white hover:bg-white/10 border-0 disabled:opacity-40"
                >
                  Revert to draft
                </button>
              )}
            </div>
          )}
        </div>

        {/* Decision */}
        <div className="bg-slate-800/30 rounded-xl border border-white/5 p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="flex flex-col items-center">
                <span className="text-[9px] text-gray-400 uppercase">OK</span>
                <span className="text-green-400 font-bold text-sm">{countOk}</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[9px] text-gray-400 uppercase">Design</span>
                <span className="text-red-400 font-bold text-sm">{countDesign}</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[9px] text-gray-400 uppercase">CS</span>
                <span className="text-amber-400 font-bold text-sm">{countCs}</span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => rateImage('need_cs')}
                disabled={editMode || readOnly}
                title={readOnly ? 'Set is done — view only' : editMode ? 'Save or cancel the edit first' : 'Need CS — customer service (low res / support)'}
                className={`w-12 h-12 flex items-center justify-center p-0 rounded-full border-0 ring-1 ring-amber-500/30 transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
                  currentReview?.decision === 'need_cs'
                    ? 'bg-amber-500 text-white ring-amber-400'
                    : 'bg-amber-900/30 text-amber-400 hover:bg-amber-500 hover:text-white hover:ring-amber-400'
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => rateImage('need_design')}
                disabled={editMode || readOnly}
                title={readOnly ? 'Set is done — view only' : editMode ? 'Save or cancel the edit first' : 'Need design — send back to the designers'}
                className={`w-12 h-12 flex items-center justify-center p-0 rounded-full border-0 ring-1 ring-red-500/30 transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
                  currentReview?.decision === 'need_design'
                    ? 'bg-red-600 text-white ring-red-400'
                    : 'bg-red-900/30 text-red-400 hover:bg-red-500 hover:text-white hover:ring-red-400'
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 4h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"
                  />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => rateImage('ok')}
                disabled={editMode || readOnly}
                title={readOnly ? 'Set is done — view only' : editMode ? 'Save or cancel the edit first' : 'OK — approve the print file'}
                className={`w-12 h-12 flex items-center justify-center p-0 rounded-full border-0 ring-1 ring-green-500/30 transition-all shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${
                  currentReview?.decision === 'ok'
                    ? 'bg-green-600 text-white ring-green-400'
                    : 'bg-green-900/30 text-green-400 hover:bg-green-500 hover:text-white hover:ring-green-400'
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                  />
                </svg>
              </button>
            </div>
          </div>
          {currentReview?.decision && (
            <div className="text-[11px] text-gray-400">
              Decision:{' '}
              <span className={
                currentReview.decision === 'ok' ? 'text-green-300 font-semibold'
                  : currentReview.decision === 'need_design' ? 'text-red-300 font-semibold' : 'text-amber-300 font-semibold'
              }>
                {DECISION_LABEL[currentReview.decision]}
              </span>
              {(decidedFinal === 'edit' || decidedFinal === 'remoteEdit') && <span className="text-gray-500"> · with manual edits</span>}
              {decidedFinal === 'upscaled' && <span className="text-violet-300"> · upscaled version</span>}
              {decidedFinal === 'draft' && hasUpscaled(currentReview) && <span className="text-gray-500"> · regular version</span>}
            </div>
          )}
          {currentSync && <div className={`text-[11px] mt-0.5 ${currentSync.cls}`}>{currentSync.text}</div>}
        </div>

        {/* End of batch — the next one is already on this device */}
        {allDecided && !batch.viewOnly && (
          <div className="bg-green-500/10 rounded-xl border border-green-500/30 p-3 flex flex-col gap-2">
            <div className="text-sm text-green-200 font-semibold">Set #{batch.id} reviewed</div>
            <div className="text-[11px] text-gray-400">
              {syncedCount === files.length
                ? 'All decisions are saved on the server.'
                : `Saving in the background: ${syncedCount}/${files.length} saved${syncErrorCount ? `, ${syncErrorCount} failed (retrying)` : ''}.`}
            </div>
            {nextBatch ? (
              <button
                type="button"
                onClick={() => onOpenBatch(nextBatch.id)}
                disabled={!nextProgress.complete}
                title={nextProgress.complete ? undefined : 'Opens when the whole set is in this browser'}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 rounded-lg border-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue with set #{nextBatch.id}
                <span className="font-normal text-green-100/80 text-xs ml-2">
                  {nextProgress.complete ? `${nextBatchFiles.length} files ready` : `downloading ${nextProgress.done}/${nextProgress.total} images`}
                </span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onRequestNext}
                disabled={requestingNext}
                className="w-full bg-slate-700 hover:bg-slate-600 text-white font-medium py-2.5 rounded-lg border-0 transition-colors disabled:opacity-50"
              >
                {requestingNext ? 'Requesting…' : batch.nextBatchId === null ? 'No eligible files earlier — check again' : 'Get the next set'}
              </button>
            )}
          </div>
        )}

        {/* Server history of this file (moderators) */}
        {showServerHistory && (
          <details className="bg-slate-800/30 rounded-xl border border-white/5 px-3 py-2">
            <summary className="text-xs text-gray-300 cursor-pointer select-none">
              Server history
              {serverHistory?.reviews.length
                ? ` · last review by ${serverHistory.reviews[serverHistory.reviews.length - 1].by ?? 'pipeline'}`
                : ''}
            </summary>
            <div className="mt-2 flex flex-col gap-1 text-[11px] leading-snug">
              {serverPack.error && <div className="text-red-300">{serverPack.error}</div>}
              {!serverPack.error && !serverHistory && <div className="text-gray-500 italic">{serverPack.loading ? 'Loading…' : 'Not on the server yet'}</div>}
              {serverHistory && (
                <>
                  <div><span className="text-gray-500">{fmtDateTime(serverHistory.createdAt)}</span> <span className="text-gray-200">created</span></div>
                  {serverHistory.processedAt && (
                    <div>
                      <span className="text-gray-500">{fmtDateTime(serverHistory.processedAt)}</span>{' '}
                      <span className={serverHistory.jobStatus === 'succeeded' ? 'text-gray-200' : 'text-red-300'}>
                        processed · {serverHistory.jobStatus ?? '—'}
                        {serverHistory.jobRuns > 1 ? ` · ${serverHistory.jobRuns} runs` : ''}
                        {serverHistory.jobIssue ? ` · ${serverHistory.jobIssue}` : ''}
                      </span>
                    </div>
                  )}
                  {serverHistory.reviews.map((r, i) => (
                    <div key={`${r.at}-${i}`}>
                      <span className="text-gray-500">{fmtDateTime(r.at)}</span>{' '}
                      <span className="text-gray-200">
                        {r.auto ? 'auto-reviewed' : `reviewed by ${r.by ?? '—'}`} · {r.result ? DECISION_LABEL[r.result as PrintFileReviewResult] ?? r.result : '—'}
                        {r.finalSource === 'manual_edits' ? ' · manual edits' : r.finalSource === 'draft' ? ' · draft' : ''}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </details>
        )}

        {/* Edit log of this file */}
        <details className="bg-slate-800/30 rounded-xl border border-white/5 px-3 py-2">
          <summary className="text-xs text-gray-300 cursor-pointer select-none">Edit log ({fileLogs.length})</summary>
          <div className="mt-2 max-h-[180px] overflow-y-auto flex flex-col gap-1">
            {recentLogs.length === 0 && <div className="text-[11px] text-gray-500 italic">Nothing yet</div>}
            {recentLogs.map((l) => (
              <div key={l.id ?? `${l.ts}-${l.action}`} className="text-[11px] leading-snug">
                <span className="text-gray-500">{fmtTime(l.ts)}</span>{' '}
                <span className={l.action.includes('error') ? 'text-red-300' : 'text-gray-200'}>{l.action}</span>{' '}
                <span className="text-gray-500 break-all">{fmtDetails(l.details)}</span>
              </div>
            ))}
          </div>
        </details>

      </div>
    </div>
  );
};

export default ReviewModal;
